import { motion } from 'motion/react';
import { type ClipboardEvent, type KeyboardEvent, useRef } from 'react';
import { cn } from '@/lib/cn';

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** fired once all digits are entered */
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  label?: string;
}

/** Segmented one-time-code input: auto-advance, backspace, arrows, paste and auto-submit. */
export function OtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled,
  invalid,
  autoFocus,
  label = 'Verification code',
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const focusAt = (i: number) => refs.current[Math.max(0, Math.min(length - 1, i))]?.focus();

  const commit = (next: string) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    onChange(clean);
    if (clean.length === length) onComplete?.(clean);
  };

  const setDigit = (index: number, digit: string) => {
    const arr = [...digits];
    arr[index] = digit;
    // keep digits contiguous so the value is always a prefix
    commit(arr.join('').slice(0, length));
  };

  const onKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) {
        commit(value.slice(0, i) + value.slice(i + 1));
      } else if (i > 0) {
        commit(value.slice(0, i - 1) + value.slice(i));
        focusAt(i - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    commit(pasted);
    focusAt(pasted.length);
  };

  return (
    <motion.div
      role="group"
      aria-label={label}
      className="flex justify-between gap-2 sm:gap-3"
      animate={invalid ? { x: [0, -8, 8, -5, 5, 0] } : { x: 0 }}
      transition={{ duration: 0.35 }}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          pattern="[0-9]*"
          maxLength={1}
          aria-label={`Digit ${i + 1} of ${length}`}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          value={d}
          onFocus={(e) => e.currentTarget.select()}
          onPaste={onPaste}
          onKeyDown={onKeyDown(i)}
          onChange={(e) => {
            const char = e.target.value.replace(/\D/g, '').slice(-1);
            if (!char) return;
            if (i > value.length) {
              setDigit(value.length, char);
              focusAt(value.length + 1);
            } else {
              setDigit(i, char);
              focusAt(i + 1);
            }
          }}
          className={cn(
            'tabular h-14 w-full min-w-0 rounded-lg border border-border bg-surface text-center font-display text-2xl font-semibold text-fg shadow-e1 transition-[border-color,box-shadow,transform] duration-150',
            'focus:scale-[1.04] focus:border-brand focus:ring-3 focus:ring-ring/30 focus:outline-none',
            d && 'border-border-strong',
            invalid && 'border-danger focus:ring-danger/25',
            disabled && 'opacity-60',
          )}
        />
      ))}
    </motion.div>
  );
}
